import { CONTEXT_QUERY_PARAM, OfferListContext } from '@constants/queryParams';
import {
  ADMISSION_TYPE_BOTTLE_SERVICE,
  ADMISSION_TYPE_GUEST_LIST,
  ADMISSION_TYPE_SEASON_PASS,
  ADMISSION_TYPE_TICKET,
  BottleService,
  GuestList,
  Ticket,
} from '@domain/admission';
import { EventPass } from '@domain/passes';
import { GenericQueryOptions, useGenericQuery } from '@hooks/use-generic-query';
import { request } from '@utils/request/request';
import { RequestError, RequestMethod } from '@utils/request/request.type';

import { QUERY_KEYS } from './constants';

export interface AdmissionResponse {
  [ADMISSION_TYPE_BOTTLE_SERVICE]: BottleService[] | [];
  [ADMISSION_TYPE_GUEST_LIST]: GuestList[] | [];
  [ADMISSION_TYPE_TICKET]: Ticket[] | [];
  [ADMISSION_TYPE_SEASON_PASS]: EventPass[] | [];
}

// The import context returns a role-filtered list, so it must not share a cache entry with the management screens.
export const getAdmissionsQueryKey = (eventId: string, context?: OfferListContext) =>
  context ? [QUERY_KEYS.getAdmissions, eventId, { context }] : [QUERY_KEYS.getAdmissions, eventId];

const getAdmissionsByEventId = async (eventId: string, context?: OfferListContext) => {
  const searchParams = new URLSearchParams();
  if (context) searchParams.set(CONTEXT_QUERY_PARAM, context);
  const query = searchParams.toString();
  const search = query ? `?${query}` : '';
  const url = `${import.meta.env.VITE_OFFERS_HOST}/v1/nightgraph/events/${eventId}/admissions${search}`;

  return request({ url, method: RequestMethod.GET });
};

export const useGetAdmissions = ({
  eventId,
  context,
  options,
}: {
  eventId: string;
  context?: OfferListContext;
  options?: GenericQueryOptions<AdmissionResponse, RequestError>;
}) => {
  return useGenericQuery<AdmissionResponse, RequestError>(
    getAdmissionsQueryKey(eventId, context),
    () => getAdmissionsByEventId(eventId, context),
    options
  );
};
